import { motion } from "framer-motion"
import type { ReactNode } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useThemeSurfaces } from "../theme/useThemeSurfaces"

type Column<T> = {
  key: string
  title: string
  width?: string | number
  align?: "left" | "right" | "center"
  render?: (row: T) => ReactNode
  /** Where this column lands in the ≤640px card layout.
      Defaults: first column = title, last right-aligned column = value, rest = meta. */
  mobileRole?: "title" | "value" | "meta" | "hidden"
}

type TableProps<T> = {
  data: T[]
  columns: Column<T>[]
  getRowId: (row: T) => string
  onRowClick?: (row: T) => void
  activeRowId?: string | null
  enableKeyboardNav?: boolean
  ariaLabel?: string
}

export function Table<T>({
  data,
  columns,
  getRowId,
  onRowClick,
  activeRowId,
  enableKeyboardNav = false,
  ariaLabel,
}: TableProps<T>) {
  const { table } = useThemeSurfaces()
  const containerRef = useRef<HTMLDivElement>(null)
  const [focusIndex, setFocusIndex] = useState(0)

  useEffect(() => {
    if (!enableKeyboardNav) return
    const node = containerRef.current
    if (!node) return

    function handleKey(event: KeyboardEvent) {
      if (!data.length) return
      if (event.key === "j" || event.key === "ArrowDown") {
        event.preventDefault()
        setFocusIndex((prev) => Math.min(prev + 1, data.length - 1))
      } else if (event.key === "k" || event.key === "ArrowUp") {
        event.preventDefault()
        setFocusIndex((prev) => Math.max(prev - 1, 0))
      } else if (event.key === "Enter" && onRowClick) {
        event.preventDefault()
        const row = data[focusIndex]
        if (row) onRowClick(row)
      }
    }

    node.addEventListener("keydown", handleKey)
    return () => node.removeEventListener("keydown", handleKey)
  }, [enableKeyboardNav, data, focusIndex, onRowClick])

  useEffect(() => {
    if (!enableKeyboardNav) return
    const id = activeRowId
    if (!id) return
    const idx = data.findIndex((row) => getRowId(row) === id)
    if (idx >= 0) setFocusIndex(idx)
  }, [activeRowId, data, enableKeyboardNav, getRowId])

  const handleRowClick = useCallback(
    (row: T, index: number) => {
      setFocusIndex(index)
      onRowClick?.(row)
    },
    [onRowClick],
  )

  const colStyles = useMemo(() => {
    return columns.map((col) => ({
      width: col.width,
      justifyContent:
        col.align === "right" ? "flex-end" : col.align === "center" ? "center" : "flex-start",
      textAlign: col.align ?? "left",
    }))
  }, [columns])

  const primaryColumn =
    columns.find((column) => column.mobileRole === "title") ?? columns[0]
  const trailingColumn =
    columns.find((column) => column.mobileRole === "value") ??
    (columns.length > 1 &&
    columns[columns.length - 1].align === "right" &&
    columns[columns.length - 1] !== primaryColumn &&
    !columns[columns.length - 1].mobileRole
      ? columns[columns.length - 1]
      : null)
  const metaColumns = columns.filter(
    (column) =>
      column !== primaryColumn && column !== trailingColumn && column.mobileRole !== "hidden",
  )

  const renderCell = useCallback(
    (column: Column<T>, row: T) =>
      column.render ? column.render(row) : ((row as Record<string, unknown>)[column.key] as ReactNode),
    [],
  )

  return (
    <div className="table-wrapper" style={{ width: "100%" }}>
      {/* Card list: same data as the grid, shown ≤640px instead of it. */}
      <div className="table-cards" aria-label={ariaLabel}>
        {data.map((row) => {
          const id = getRowId(row)
          const isActive = id === activeRowId
          return (
            <button
              key={id}
              type="button"
              className="mono table-card"
              data-active={isActive}
              onClick={() => handleRowClick(row, data.indexOf(row))}
              style={{
                background: isActive ? table.active : table.body,
                borderColor: table.border,
              }}
            >
              <span className="table-card__top">
                <span className="table-card__title">{renderCell(primaryColumn, row)}</span>
                {trailingColumn ? (
                  <span className="table-card__value">{renderCell(trailingColumn, row)}</span>
                ) : null}
              </span>
              {metaColumns.length ? (
                <span className="table-card__meta">
                  {metaColumns.map((column) => (
                    <span key={column.key} className="table-card__meta-item">
                      {renderCell(column, row)}
                    </span>
                  ))}
                </span>
              ) : null}
            </button>
          )
        })}
        {data.length === 0 ? (
          <div className="small" style={{ padding: "20px 4px", color: "var(--muted)" }}>
            Nothing here yet — send a text from your phone to get started.
          </div>
        ) : null}
      </div>

      <div className="table-scroll" style={{ overflowX: "auto", width: "100%" }}>
        <div
          ref={containerRef}
          tabIndex={enableKeyboardNav ? 0 : -1}
          role="grid"
          aria-label={ariaLabel}
          style={{
            display: "grid",
            gridTemplateRows: "auto 1fr",
            background: table.frame,
            border: `1px solid ${table.border}`,
            overflow: "hidden",
            minWidth: "max(520px, 100%)",
          }}
        >
          <div
            role="row"
            className="mono small"
            style={{
              display: "grid",
              gridTemplateColumns: columns.map((col) => col.width ?? "1fr").join(" "),
              background: table.head,
              padding: "12px 18px",
              letterSpacing: "0.16em",
            }}
          >
            {columns.map((column, index) => (
              <span key={column.key} style={{ ...colStyles[index], display: "flex", alignItems: "center" }}>
                {column.title}
              </span>
            ))}
          </div>

          <div
            role="rowgroup"
            style={{
              background: table.body,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {data.map((row, index) => {
              const id = getRowId(row)
              const isActive = id === activeRowId
              const isFocused = index === focusIndex
              return (
                <motion.button
                  key={id}
                  type="button"
                  role="row"
                  onClick={() => handleRowClick(row, index)}
                  className="mono"
                  style={{
                    display: "grid",
                    gridTemplateColumns: columns.map((col) => col.width ?? "1fr").join(" "),
                    gap: "12px",
                    padding: "14px 18px",
                    alignItems: "center",
                    border: "none",
                    background: isActive ? table.active : "transparent",
                    color: "inherit",
                    textAlign: "left",
                    cursor: "pointer",
                    position: "relative",
                  }}
                  whileHover={{ backgroundColor: table.hover }}
                  data-active={isActive}
                  data-focused={isFocused}
                >
                  {isActive ? (
                    <span
                      aria-hidden
                      style={{
                        position: "absolute",
                        inset: "4px 0 4px auto",
                        width: "2px",
                        background: "var(--accent)",
                      }}
                    />
                  ) : null}
                  {columns.map((column, colIndex) => (
                    <span
                      key={column.key}
                      style={{
                        ...colStyles[colIndex],
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontFamily:
                          colIndex === 0
                            ? '"IBM Plex Mono","Space Mono",ui-monospace,Menlo,Consolas,monospace'
                            : undefined,
                      }}
                    >
                      {column.render
                        ? column.render(row)
                        : ((row as Record<string, unknown>)[column.key] as ReactNode)}
                    </span>
                  ))}
                </motion.button>
              )
            })}
            {data.length === 0 ? (
              <div className="small" style={{ padding: "24px", color: "var(--muted)" }}>
                Nothing here yet — send a text from your phone to get started.
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Table
